import type { SxProps, Theme } from '@mui/material';

/**
 * Shared MUI sx layout mixins.
 *
 * Usage:
 *   import { sxColFillHidden } from '../../theme/sxMixins';
 *
 *   <Box sx={sxColFillHidden} />
 *   <Box sx={{ ...sxColFillHidden, p: 1 }} />
 */

// ─── primitives ──────────────────────────────────────────────────────────────

/** display: flex + flex-direction: column */
export const sxFlexCol: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

/**
 * Fills available vertical space without clipping.
 * Use on a flex child that should grow and not create a scrollbar itself.
 */
export const sxFill: SxProps<Theme> = {
  flexGrow: 1,
  minHeight: 0,
  minWidth: 0,
};

// ─── composites ──────────────────────────────────────────────────────────────

/**
 * Flex column that fills its parent.
 * Most common pattern — replaces all of these variants:
 *
 *   { display:'flex', flexDirection:'column', flexGrow:1, minHeight:0, minWidth:0 }
 *   { display:'flex', flexDirection:'column', flex:1, minHeight:0, minWidth:0 }
 */
export const sxColFill: SxProps<Theme> = {
  ...sxFlexCol,
  ...sxFill,
};

/**
 * Flex column that fills its parent AND hides overflow.
 * Use when the child itself manages scrolling (virtualised grids, detail forms, etc.)
 *
 *   { display:'flex', flexDirection:'column', flexGrow:1, minHeight:0, minWidth:0, overflow:'hidden' }
 *   { display:'flex', flexDirection:'column', flex:'1 1 0', minHeight:0, overflow:'hidden' }
 */
export const sxColFillHidden: SxProps<Theme> = {
  ...sxColFill,
  overflow: 'hidden',
};

/**
 * Flex column that fills its parent AND scrolls its own overflow.
 * Use when the child itself manages scrolling (virtualised grids, detail forms, etc.)
 */
export const sxColFillScroll: SxProps<Theme> = {
  ...sxColFill,
  overflow: 'auto',
};

/**
 * Full-size flex column (width + height 100 %) that hides overflow.
 * For top-level page wrappers / Container replacements.
 *
 *   { width:'100%', height:'100%', display:'flex', flexDirection:'column',
 *     minHeight:0, overflow:'hidden' }
 */
export const sxColFullHidden: SxProps<Theme> = {
  ...sxFlexCol,
  width: '100%',
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
};

/** Dark success Alert */
export const sxSuccessAlert: SxProps<Theme> = {
  width: '100%',
  backgroundColor: '#323232',
  color: '#fff',
  '& .MuiAlert-icon': { color: '#fff' },
  '& .MuiIconButton-root': { color: '#fff' },
};
